import { ICustomerType } from '../constants/customer';
import {
  IEditMatterRequestDto,
  IMatterRequest,
  IMatterRequestParams,
} from '../interface/matterRequest';
import dayjs from 'dayjs';

function addPropertyIfChanged<T>(
  currentValue: T,
  originalValue: T,
  propertyName: keyof IEditMatterRequestDto,
): Partial<IEditMatterRequestDto> {
  return currentValue === originalValue
    ? {}
    : ({ [propertyName]: currentValue } as Partial<IEditMatterRequestDto>);
}

/**
 * Distinguishes "field was never set on either side" (no-op) from "field was
 * explicitly cleared" (must send `null` so the backend drops the value) —
 * a plain `?? null` fallback would misreport the former as a change.
 */
function resolveClearableValue<T>(currentValue: T | undefined, originalValue: T | undefined) {
  if (currentValue !== undefined) return currentValue;
  return originalValue !== undefined ? null : undefined;
}

/** Owner and responsible are recommended, not required — a cleared one must not be refilled. */
function buildOwnerAndResponsible(matterRequest: IMatterRequest | null) {
  return {
    ...(matterRequest?.responsibleUserId
      ? { responsibleUserId: matterRequest.responsibleUserId }
      : {}),
    ...(matterRequest?.ownerUserId ? { ownerUserId: matterRequest.ownerUserId } : {}),
  };
}

export function buildCreateDtoForExisting(matterRequest: IMatterRequest): IMatterRequestParams {
  return {
    subject: matterRequest?.subject ?? '',
    description: matterRequest?.description ?? '',
    receivingDate: dayjs().format('YYYY-MM-DD'),
    ...buildOwnerAndResponsible(matterRequest),
    insider: false,
    ...(matterRequest?.principalCompanyId && matterRequest.principalCompanyId
      ? { principalCompanyId: matterRequest.principalCompanyId }
      : {}),
    ...(matterRequest?.principalPrivatePersonId && matterRequest.principalPrivatePersonId
      ? { principalPrivatePersonId: matterRequest.principalPrivatePersonId }
      : {}),
    ...(matterRequest?.matterRequestType?.id
      ? { matterRequestTypeId: matterRequest.matterRequestType.id }
      : {}),
    ...(matterRequest?.language ? { language: matterRequest.language } : {}),
    ...(matterRequest?.currency ? { currency: matterRequest.currency } : {}),
    ...(matterRequest?.businessAreaId ? { businessAreaId: matterRequest.businessAreaId } : {}),
  };
}

export function buildCreateDtoForNew(
  matterRequest: IMatterRequest | null,
  userId: string,
): IMatterRequestParams {
  const base = {
    subject: matterRequest?.subject ?? '',
    description: matterRequest?.description ?? '',
    receivingDate: dayjs().format('YYYY-MM-DD'),
    ...buildOwnerAndResponsible(matterRequest),
    insider: false,
    ...(matterRequest?.matterRequestType?.id
      ? { matterRequestTypeId: matterRequest.matterRequestType.id }
      : {}),
    ...(matterRequest?.language ? { language: matterRequest.language } : {}),
    ...(matterRequest?.currency ? { currency: matterRequest.currency } : {}),
    ...(matterRequest?.businessAreaId ? { businessAreaId: matterRequest.businessAreaId } : {}),
  };

  if (matterRequest?.newCustomer?.type === ICustomerType.COMPANY) {
    return {
      ...base,
      newCustomer: {
        name: matterRequest?.newCustomer?.name ?? '',
        homeCountry: matterRequest?.newCustomer?.homeCountry ?? '',
        responsibleUserId: userId,
      },
    };
  } else {
    return {
      ...base,
      newPrivatePerson: {
        firstNames: matterRequest?.newCustomer?.name ?? '',
        lastName: matterRequest?.newCustomer?.lastName ?? '',
        homeCountry: matterRequest?.newCustomer?.homeCountry ?? '',
        responsibleUserId: userId,
      },
    };
  }
}

export function buildEditDto(
  matterRequest: IMatterRequest | null,
  existingMatterRequest: IMatterRequest,
): IEditMatterRequestDto {
  const selectedCustomer = matterRequest?.principalCompany ?? matterRequest?.principalPrivatePerson;
  const customerChanged =
    selectedCustomer?.id &&
    selectedCustomer.id !==
      (existingMatterRequest.principalCompanyId ?? existingMatterRequest.principalPrivatePersonId);

  const isCompany = !!matterRequest?.principalCompany;

  return {
    version: existingMatterRequest.version,
    ...(customerChanged
      ? {
          principalCompanyId: isCompany ? selectedCustomer.id : undefined,
          principalPrivatePersonId: isCompany ? undefined : selectedCustomer.id,
        }
      : {}),
    ...addPropertyIfChanged(matterRequest?.subject, existingMatterRequest.subject, 'subject'),
    ...addPropertyIfChanged(
      matterRequest?.description,
      existingMatterRequest.description,
      'description',
    ),
    ...addPropertyIfChanged(
      matterRequest?.matterRequestTypeId ?? null,
      existingMatterRequest.matterRequestType?.id,
      'matterRequestTypeId',
    ),
    ...addPropertyIfChanged(
      matterRequest?.language ?? null,
      existingMatterRequest.language,
      'language',
    ),
    ...addPropertyIfChanged(
      matterRequest?.currency ?? null,
      existingMatterRequest.currency,
      'currency',
    ),
    ...addPropertyIfChanged(
      resolveClearableValue(
        matterRequest?.responsibleUserId,
        existingMatterRequest.responsibleUserId,
      ),
      existingMatterRequest.responsibleUserId,
      'responsibleUserId',
    ),
    ...addPropertyIfChanged(
      resolveClearableValue(matterRequest?.ownerUserId, existingMatterRequest.ownerUserId),
      existingMatterRequest.ownerUserId,
      'ownerUserId',
    ),
    ...addPropertyIfChanged(
      resolveClearableValue(matterRequest?.businessAreaId, existingMatterRequest.businessAreaId),
      existingMatterRequest.businessAreaId,
      'businessAreaId',
    ),
  };
}

/**
 * Fields with an `isXSetByUser` flag can be auto-filled on mount (mail-subject
 * prefill, default owner/responsible/type/language/currency/business area),
 * so their raw value can't be told apart from a default the user never
 * touched — the flag is checked instead. Fields without such a flag
 * (description, customer selection, new-customer inputs) are only ever set
 * via direct user interaction, so raw presence is still a safe signal.
 */
export function checkHasFieldsChanged(matterRequest: IMatterRequest | null): boolean {
  const selectedCustomer = matterRequest?.principalCompany ?? matterRequest?.principalPrivatePerson;
  return !!(
    matterRequest?.isSubjectSetByUser ||
    matterRequest?.description ||
    selectedCustomer ||
    matterRequest?.newCustomer?.name ||
    matterRequest?.newCustomer?.lastName ||
    matterRequest?.newCustomer?.homeCountry ||
    matterRequest?.isMatterTypeSetByUser ||
    matterRequest?.isLanguageSetByUser ||
    matterRequest?.isCurrencySetByUser ||
    matterRequest?.isResponsibleSetByUser ||
    matterRequest?.isOwnerSetByUser ||
    matterRequest?.isBusinessAreaSetByUser
  );
}
